import { id } from "@instantdb/react";
import { effect, signal } from "@preact/signals";
import { coerce } from "@uri/gamla";
import { apiClient, httpCommunication } from "@uri/typed-api";
import { render } from "preact";
import { ErrorBoundary, LocationProvider, Route, Router } from "preact-iso";
import { useEffect, useState } from "preact/hooks";
import { toast, Toaster } from "react-hot-toast";
import { apiDefinition } from "../../client/src/apiTyping.ts";
import { apiBaseUrl } from "../../client/src/main.ts";
import { whatsappAccountStatus } from "../../instant.schema.ts";
import {
  queryOnce,
  transact,
  tx,
  useAuth,
  useQuery,
  userIdDeFacto,
} from "./db.ts";
import { Login } from "./login.tsx";
import {
  Button,
  ErrorMessage,
  LoadingIndication,
  QrWithTimer,
  RelatedServices,
  SecretToken,
} from "./uiComponents.tsx";
import { ApiDocs } from "./apiDocs.tsx";

export const blurMode = signal<boolean>(false);

effect(() => {
  if (blurMode.value) {
    document.body.classList.add("blurred");
  } else {
    document.body.classList.remove("blurred");
  }
});

if ("serviceWorker" in navigator) {
  navigator.serviceWorker.register("/service-worker.js");
}

const wrapperStyle = {
  display: "flex",
  flexDirection: "column",
  overflowY: "auto",
  flexGrow: 1,
  width: "100%",
  height: "100vh", // Ensure wrapper fills viewport
  minHeight: 0, // Prevent flex children from growing unexpectedly
};

// Action row for account controls; wraps on narrow screens
const accountActionRowStyle = {
  display: "flex",
  gap: 8,
  flexDirection: "row",
  flexWrap: "wrap",
  marginTop: 10,
  paddingTop: 10,
  borderTop: "1px solid var(--border)",
} as const;

// Connection help section styles and content
const connectHelpStyle = {
  marginTop: 12,
  padding: 12,
  border: "1px solid var(--border)",
  borderRadius: "var(--radius)",
  background: "var(--surface-alt)",
  display: "flex",
  flexDirection: "column",
  gap: 8,
} as const;

const adminChipStyle = {
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  background: "var(--surface-alt)",
  border: "1px solid var(--border)",
  borderRadius: "var(--radius)",
  padding: "2px 8px",
} as const;

export const ConnectHelp = () => {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <div style={connectHelpStyle}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          flexWrap: "wrap",
        }}
      >
        <div
          style={{
            fontWeight: 600,
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
          }}
        >
          ❓ Connection Help
        </div>
        <Button
          className="secondary"
          style={{ fontSize: 12, padding: "4px 10px" }}
          onClick={() => setIsOpen(!isOpen)}
        >
          {isOpen ? "Hide" : "Show"}
        </Button>
      </div>
      {isOpen && (
        <ul
          style={{
            margin: 0,
            paddingLeft: 18,
            display: "flex",
            flexDirection: "column",
            gap: 6,
          }}
        >
          <li>
            4️⃣ WhatsApp supports up to four linked devices. In WhatsApp on your
            phone: Settings → Linked Devices. If you're at the limit, disconnect
            one and try again.
          </li>
          <li>
            🔳 QR codes expire quickly. If the QR disappears or fails, refresh
            and press <strong>Send QR Code</strong> again.
          </li>
          <li>
            📨 If you can't scan a QR, or you're connecting on the same device,
            use <strong>Send Login Code</strong>.
          </li>
          <li>
            🔄 It may take a few attempts. If a try fails, trigger{" "}
            <strong>Send QR Code</strong> or <strong>Send Login Code</strong>
            {" "}
            again.
          </li>
          <li>
            📶 Make sure WhatsApp on your phone is online, the app is up to
            date, and battery saver/background restrictions aren’t blocking it.
          </li>
          <li>
            🧹 If you get stuck, use <strong>Reset</strong>{" "}
            to clear credentials, then try connecting again.
          </li>
        </ul>
      )}
    </div>
  );
};

const Header = () => (
  <div
    style={{
      padding: "10px 20px",
      backgroundColor: "#282c34",
      color: "white",
      display: "flex",
      alignItems: "center",
      gap: "24px",
    }}
  >
    <h1 style={{ margin: 0 }}>Supergreen</h1>
    <nav style={{ display: "flex", gap: "16px", fontSize: "14px" }}>
      <a
        href="/"
        style={{ color: "white", textDecoration: "none", opacity: 0.9 }}
        onMouseOver={(e) => {
          e.currentTarget.style.opacity = "1";
        }}
        onMouseOut={(e) => {
          e.currentTarget.style.opacity = "0.9";
        }}
      >
        Dashboard
      </a>
      <a
        href="/docs"
        style={{ color: "white", textDecoration: "none", opacity: 0.9 }}
        onMouseOver={(e) => {
          e.currentTarget.style.opacity = "1";
        }}
        onMouseOut={(e) => {
          e.currentTarget.style.opacity = "0.9";
        }}
      >
        API Docs
      </a>
    </nav>
  </div>
);

const emailToCreateUserFor = signal<string | null>(null);

const client = apiClient(httpCommunication(apiBaseUrl), apiDefinition);

const normalizeWaId = (input: string) => {
  const s = input.trim();
  if (!s) return s;
  // If user already provided a full chat id (contains @), keep as-is
  if (s.includes("@")) return s;
  const digits = s.replace(/\D/g, "");
  return `${digits}@c.us`;
};

const getToken = async (phoneNumber: string) => {
  const { data } = await queryOnce({
    whatsappAccounts: { $: { where: { phoneNumber } } },
  });
  return data?.whatsappAccounts?.[0]?.secretToken || "";
};

const addAdmin = async (
  accountId: string,
  existingEmails: ReadonlyArray<string>,
) => {
  const raw = prompt("Enter admin email:");
  if (!raw) return;
  const email = raw.trim();
  if (!email) return;
  if (existingEmails.includes(email)) {
    toast.error("Admin already added");
    return;
  }
  const { data } = await queryOnce({
    users: { $: { where: { email } } },
  });
  const found = data?.users?.[0];
  if (!found) {
    const shouldCreate = confirm(
      "User not found. Create new user and add as admin?",
    );
    if (!shouldCreate) return;
    const newUserId = id();
    const promise = transact([
      tx.users[newUserId].update({
        email,
        lastSeen: Date.now(),
        pushSubscriptions: {},
      }),
      tx.whatsappAccounts[accountId].link({ admins: newUserId }),
    ]);
    await toast.promise(promise, {
      loading: "Adding admin...",
      success: "Admin added",
      error: (e) => e?.message || "Failed to add admin",
    });
    return;
  }
  const promise = transact(
    tx.whatsappAccounts[accountId].link({ admins: found.id }),
  );
  await toast.promise(promise, {
    loading: "Adding admin...",
    success: "Admin added",
    error: (e) => e?.message || "Failed to add admin",
  });
};

const removeAdmin = async (
  accountId: string,
  adminId: string,
  adminEmail?: string,
) => {
  const ok = confirm(
    `Remove ${adminEmail ?? "admin"} from this WhatsApp account?`,
  );
  if (!ok) return;
  const promise = transact(
    tx.whatsappAccounts[accountId].unlink({ admins: adminId }),
  );
  await toast.promise(promise, {
    loading: "Removing admin...",
    success: "Admin removed",
    error: (e) => e?.message || "Failed to remove admin",
  });
};

const sendTest = async (fromNumber: string) => {
  const userId = userIdDeFacto.value;
  let defaultValue = "";
  if (userId) {
    const { data } = await queryOnce({
      users: { $: { where: { id: userId } } },
    });
    if (data?.users?.[0]?.whatsapp) {
      defaultValue = data.users[0].whatsapp;
    }
  }
  const raw = prompt(
    "Enter destination phone (digits or full chat id):",
    defaultValue,
  );
  if (!raw) return;
  const toNumber = normalizeWaId(raw);
  if (!toNumber) {
    toast.error("No phone number provided");
    return;
  }
  const token = await getToken(fromNumber);
  const promise = client({
    endpoint: "sendMessage",
    payload: {
      fromNumber,
      toNumber,
      message: "Hello World!",
      linkPreview: false,
      token,
    },
  });
  await toast.promise(promise, {
    loading: "Sending...",
    success: () => "Message sent",
    error: (e) =>
      e?.message ? `Failed: ${e.message}` : "Failed to send message",
  });
};

const reconnect = async (number: string) => {
  const token = await getToken(number);
  const promise = client({ endpoint: "reconnect", payload: { number, token } });
  await toast.promise(promise, {
    loading: "Reconnecting...",
    success: () => "Reconnect triggered",
    error: (e) =>
      e?.message ? `Failed: ${e.message}` : "Failed to trigger reconnect",
  });
};

const triggerCodeLogin = async (number: string) => {
  const token = await getToken(number);
  toast.promise(
    client({ endpoint: "triggerCodeLogin", payload: { number, token } }),
    {
      loading: "Generating code...",
      success: () => "Code login triggered",
      error: (e) =>
        e?.message ? `Failed: ${e.message}` : "Failed to trigger code login",
    },
  );
};

const changeAccountName = async (phoneNumber: string) => {
  const name = prompt("Enter new WhatsApp account name:");
  if (!name || !name.trim()) return;
  await toast.promise(
    client({
      endpoint: "changeWhatsappAccountName",
      payload: {
        phoneNumber,
        name: name.trim(),
        token: await getToken(phoneNumber),
      },
    }),
    {
      loading: "Changing account name...",
      success: () => "Account name changed",
      error: (e) =>
        e?.message ? `Failed: ${e.message}` : "Failed to change account name",
    },
  );
};

const changeAccountAvatar = (phoneNumber: string) => {
  const input = document.createElement("input");
  input.type = "file";
  input.accept = "image/*";
  input.onchange = (e) => {
    const file = (e.target as HTMLInputElement).files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async (event) => {
      const dataUrl = event.target?.result as string;
      if (!dataUrl) {
        toast.error("Failed to read image");
        return;
      }
      const token = await getToken(phoneNumber);
      const promise = client({
        endpoint: "changeWhatsappAccountAvatar",
        payload: { phoneNumber, imageBase64: dataUrl, token },
      });
      await toast.promise(promise, {
        loading: "Uploading avatar...",
        success: () => "Avatar updated",
        error: (e) =>
          e?.message ? `Failed: ${e.message}` : "Failed to update avatar",
      });
    };
    reader.readAsDataURL(file);
  };
  input.click();
};

const editAccountName = async (
  accountId: string,
  currentName: string,
) => {
  const raw = prompt("Edit account name:", currentName);
  if (raw === null) return;
  const newName = raw.trim();
  if (newName === currentName) return;
  const promise = transact(
    tx.whatsappAccounts[accountId].update({ name: newName }),
  );
  await toast.promise(promise, {
    loading: "Saving name...",
    success: "Account name updated",
    error: (e) => e?.message || "Failed to update account name",
  });
};

const editAccountNotes = async (
  accountId: string,
  currentNotes?: string,
) => {
  const raw = prompt("Edit notes:", currentNotes ?? "");
  if (raw === null) return;
  const trimmed = raw.trim();
  const notes = trimmed === "" ? null : trimmed;
  const promise = transact(
    tx.whatsappAccounts[accountId].update({ notes }),
  );
  await toast.promise(promise, {
    loading: "Saving notes...",
    success: () => (notes ? "Notes updated" : "Notes removed"),
    error: (e) => e?.message || "Failed to update notes",
  });
};

const WebhookEditor = (
  { phoneNumber, webhook }: { phoneNumber: string; webhook?: string },
) => {
  const [currentWebhook, setCurrentWebhook] = useState(webhook ?? "");
  // For optimistic UI, but we rely on parent refresh for now
  return (
    <div style={{ marginTop: 6 }}>
      {currentWebhook
        ? (
          <div
            style={{
              fontSize: 12,
              opacity: 0.75,
              wordBreak: "break-all",
              display: "flex",
              flexDirection: "column",
              gap: 4,
            }}
          >
            <span>Webhook: {currentWebhook}</span>
            <div style={{ display: "flex", gap: 6 }}>
              <Button
                className="secondary"
                onClick={async () => {
                  const url = prompt(
                    "Edit webhook URL (leave blank to remove)",
                    currentWebhook,
                  );
                  if (url === null) return;
                  const trimmed = url.trim();
                  const payloadUrl = trimmed === "" ? null : trimmed;
                  const token = await getToken(phoneNumber);
                  const promise = client({
                    endpoint: "setWebhook",
                    payload: { phoneNumber, url: payloadUrl, token },
                  });
                  await toast.promise(promise, {
                    loading: "Saving webhook...",
                    success: () =>
                      payloadUrl ? "Webhook updated" : "Webhook removed",
                    error: (e) => e?.message || "Failed to save webhook",
                  });
                  setCurrentWebhook(payloadUrl ?? "");
                }}
              >
                ✏ Edit
              </Button>
              <Button
                className="secondary"
                onClick={async () => {
                  if (!confirm("Remove webhook?")) return;
                  const token = await getToken(phoneNumber);
                  const promise = client({
                    endpoint: "setWebhook",
                    payload: { phoneNumber, url: null, token },
                  });
                  await toast.promise(promise, {
                    loading: "Removing...",
                    success: () => "Webhook removed",
                    error: (e) => e?.message || "Failed to remove webhook",
                  });
                  setCurrentWebhook("");
                }}
              >
                🗑 Remove
              </Button>
            </div>
          </div>
        )
        : (
          <Button
            className="secondary"
            style={{ fontSize: 12 }}
            onClick={async () => {
              const url = prompt("Enter webhook URL:");
              if (!url) return;
              const trimmed = url.trim();
              if (!trimmed) return;
              const token = await getToken(phoneNumber);
              const promise = client({
                endpoint: "setWebhook",
                payload: { phoneNumber, url: trimmed, token },
              });
              await toast.promise(promise, {
                loading: "Saving webhook...",
                success: () => "Webhook saved",
                error: (e) => e?.message || "Failed to save webhook",
              });
              setCurrentWebhook(trimmed);
            }}
          >
            ➕ Add webhook
          </Button>
        )}
    </div>
  );
};

const UserProfile = () => {
  const userId = userIdDeFacto.value;
  if (!userId) return null;
  const { data, error, isLoading } = useQuery({
    users: { $: { where: { id: userId } } },
  });
  if (error) return <ErrorMessage error={error} />;
  if (isLoading) return <LoadingIndication text="Loading profile..." />;
  if (!data?.users || data.users.length === 0) return null;
  const user = data.users[0];

  return (
    <div
      style={{ padding: 20, display: "flex", flexDirection: "column", gap: 12 }}
    >
      <h2 style={{ margin: 0 }}>Your Profile</h2>
      <div
        style={{
          border: "1px solid var(--border)",
          borderRadius: "var(--radius)",
          padding: 12,
          background: "var(--surface-alt)",
          display: "flex",
          flexDirection: "column",
          gap: 8,
        }}
      >
        <div style={{ fontSize: 14, opacity: 0.85 }}>
          Email: {user.email}
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: 14, opacity: 0.85 }}>
              Your Personal WhatsApp:
            </span>
            <span style={{ fontWeight: 600 }}>
              {user.whatsapp || "Not set"}
            </span>
            <Button
              className="secondary"
              style={{ fontSize: 12, padding: "4px 12px" }}
              onClick={async () => {
                const raw = prompt(
                  "Enter your WhatsApp number (e.g., 972501234567):",
                  user.whatsapp || "",
                );
                if (raw === null) return;
                const trimmed = raw.trim();
                const whatsapp = trimmed === "" ? null : trimmed;
                const promise = transact(
                  tx.users[user.id].update({ whatsapp }),
                );
                await toast.promise(promise, {
                  loading: "Saving...",
                  success: () =>
                    whatsapp
                      ? "WhatsApp number updated"
                      : "WhatsApp number removed",
                  error: (e) => e?.message || "Failed to save",
                });
              }}
            >
              {user.whatsapp ? "✏ Edit" : "➕ Add"}
            </Button>
          </div>
          <div style={{ fontSize: 12, opacity: 0.7, marginTop: 2 }}>
            This is your personal number for receiving test messages, not your
            bot's number
          </div>
        </div>
      </div>
    </div>
  );
};

const WhatsappAccounts = () => {
  const { data, error, isLoading } = useQuery({
    whatsappAccounts: { admins: {} },
  });
  if (error) return <ErrorMessage error={error} />;
  if (isLoading) return <LoadingIndication text="Loading bots..." />;
  const whatsappAccounts = data.whatsappAccounts;

  const formatStatusHistory = (
    history: Array<{ status: string; timestamp: number }> | undefined,
  ) => {
    const [showHistory, setShowHistory] = useState(false);
    if (!history || history.length === 0) return null;
    return (
      <div style={{ marginTop: 10, fontSize: 12 }}>
        <button
          type="button"
          onClick={() => setShowHistory(!showHistory)}
          style={{
            background: "none",
            border: "none",
            color: "inherit",
            cursor: "pointer",
            fontWeight: 600,
            fontSize: 12,
            padding: 0,
            opacity: 0.9,
            textDecoration: "underline",
          }}
        >
          {showHistory ? "Hide" : "Show"} Status History
        </button>
        {showHistory && (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 4,
              maxHeight: "200px",
              overflowY: "auto",
              padding: "8px",
              background: "rgba(0,0,0,0.2)",
              borderRadius: "var(--radius)",
              marginTop: 6,
            }}
          >
            {[...history].reverse().map((entry, idx) => (
              <div key={idx} style={{ opacity: 0.85, fontSize: 11 }}>
                <span
                  style={{
                    color: entry.status === whatsappAccountStatus.connected ||
                        entry.status === "isLogged"
                      ? "#2e7d32"
                      : entry.status === whatsappAccountStatus.reconnecting
                      ? "#f57c00"
                      : entry.status === whatsappAccountStatus.podIsDown
                      ? "#ff5555"
                      : "#c62828",
                    fontWeight: 500,
                  }}
                >
                  {entry.status}
                </span>{" "}
                <span style={{ opacity: 0.7 }}>
                  {new Date(entry.timestamp).toLocaleString()}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };
  return (
    <div
      style={{ padding: 20, display: "flex", flexDirection: "column", gap: 12 }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <h2 style={{ margin: 0 }}>Your whatsapp accounts</h2>
        <Button
          className="primary"
          style={{ fontSize: 12, padding: "4px 12px" }}
          onClick={async () => {
            const raw = prompt("Enter WhatsApp number (digits only):");
            if (!raw) return;
            const phoneNumber = raw.replace(/\D/g, "");
            if (!phoneNumber) {
              toast.error("Invalid number");
              return;
            }

            const { data } = await queryOnce({
              whatsappAccounts: { $: { where: { phoneNumber } } },
            });

            if (data.whatsappAccounts.length > 0) {
              toast.error("Account already exists");
              return;
            }

            const newId = id();
            const userId = userIdDeFacto.value;
            if (!userId) {
              toast.error("User not found");
              return;
            }

            const promise = transact([
              tx.whatsappAccounts[newId].update({
                phoneNumber,
                name: phoneNumber,
                isConnected: false,
                statusHistory: [],
                secretToken: crypto.randomUUID(),
              }),
              tx.whatsappAccounts[newId].link({ admins: userId }),
            ]);
            await toast.promise(promise, {
              loading: "Adding account...",
              success: "Account added",
              error: "Failed to add account",
            });
          }}
        >
          ➕ Add
        </Button>
      </div>
      <ConnectHelp />
      {whatsappAccounts.length === 0 ? <div>No bots yet.</div> : (
        <ul
          style={{
            listStyle: "none",
            padding: 0,
            margin: 0,
            display: "flex",
            flexDirection: "column",
            gap: 8,
          }}
        >
          {whatsappAccounts.map((b) => (
            <li
              key={b.id}
              style={{
                border: "1px solid var(--border)",
                borderRadius: "var(--radius)",
                padding: 12,
                background: "var(--surface-alt)",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  marginBottom: 4,
                }}
              >
                <div style={{ fontWeight: 600 }}>{b.name}</div>
                <Button
                  className="secondary"
                  style={{ fontSize: 12, padding: "2px 8px" }}
                  onClick={() => editAccountName(b.id, b.name)}
                >
                  ✏ Edit
                </Button>
              </div>
              <div style={{ fontSize: 14, opacity: 0.85 }}>
                Number: {b.phoneNumber}
              </div>
              {b.secretToken && <SecretToken token={b.secretToken} />}
              <div style={{ marginTop: 6, fontSize: 14 }}>
                Status:{" "}
                <span
                  style={{
                    color: !b.isConnected ? "#c62828" : "#2e7d32",
                    fontWeight: 600,
                  }}
                >
                  {!b.isConnected ? "Disconnected" : "Connected"}
                </span>
              </div>
              {b.lastEventTime && (
                <div style={{ marginTop: 4, fontSize: 12, opacity: 0.8 }}>
                  Last event: {new Date(b.lastEventTime).toLocaleString()}
                </div>
              )}
              {formatStatusHistory(b.statusHistory)}
              <WebhookEditor phoneNumber={b.phoneNumber} webhook={b.webhook} />
              <div
                style={{
                  marginTop: 6,
                  fontSize: 12,
                  opacity: 0.85,
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  flexWrap: "wrap",
                }}
              >
                <span>Admins:</span>
                {(b.admins && b.admins.length > 0)
                  ? b.admins.map((a: { id: string; email: string }) => (
                    <span style={adminChipStyle}>
                      {a.email}
                      <Button
                        className="secondary"
                        style={{ fontSize: 10, padding: "2px 6px" }}
                        onClick={() =>
                          removeAdmin(b.id, a.id, a.email)}
                      >
                        🗑 Remove
                      </Button>
                    </span>
                  ))
                  : <span>None</span>}
                <Button
                  className="secondary"
                  style={{ fontSize: 12, padding: "4px 10px" }}
                  onClick={() =>
                    addAdmin(
                      b.id,
                      (b.admins || []).map((a: { email: string }) => a.email),
                    )}
                >
                  ➕ Add Admin
                </Button>
              </div>
              <div
                style={{
                  marginTop: 6,
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  flexWrap: "wrap",
                }}
              >
                <span
                  style={{
                    fontSize: 12,
                    opacity: 0.85,
                    whiteSpace: "pre-wrap",
                  }}
                >
                  {b.notes ? `Notes: ${b.notes}` : "No notes"}
                </span>
                <Button
                  className="secondary"
                  style={{ fontSize: 10, padding: "2px 6px" }}
                  onClick={() => editAccountNotes(b.id, b.notes)}
                >
                  {b.notes ? "✏ Edit Notes" : "➕ Add Notes"}
                </Button>
              </div>
              {!b.isConnected &&
                b.verificationCode && (
                <div style={{ marginTop: 10 }}>
                  <QrWithTimer
                    size={180}
                    url={b.verificationCode}
                    issuedAt={coerce(b.qrIssuedAt)}
                  />
                  <div style={{ fontSize: 12, opacity: 0.7, marginTop: 4 }}>
                    Scan with WhatsApp on your phone
                  </div>
                </div>
              )}
              {!b.isConnected &&
                b.pairingCode && (
                <div style={{ marginTop: 10 }}>
                  <div
                    style={{
                      fontSize: 32,
                      fontWeight: 600,
                      letterSpacing: 8,
                      padding: "20px",
                      border: "2px solid var(--border)",
                      borderRadius: "var(--radius)",
                      background: "var(--surface-alt)",
                      textAlign: "center",
                      fontFamily: "monospace",
                    }}
                  >
                    {b.pairingCode}
                  </div>
                  <div style={{ fontSize: 12, opacity: 0.7, marginTop: 4 }}>
                    Enter this code in WhatsApp: Settings → Linked Devices →
                    Link a Device → Link with phone number
                  </div>
                </div>
              )}
              {!b.isConnected &&
                !b.verificationCode &&
                !b.pairingCode && (
                <div style={{ marginTop: 8, fontSize: 12, opacity: 0.75 }}>
                  Waiting for QR code or pairing code...
                </div>
              )}
              <div
                style={accountActionRowStyle}
              >
                <Button
                  className="primary"
                  onClick={() => sendTest(b.phoneNumber)}
                >
                  ▶ Send test
                </Button>
                <Button
                  className="secondary"
                  onClick={() => reconnect(b.phoneNumber)}
                >
                  🔳 Send QR Code
                </Button>
                <Button
                  className="secondary"
                  onClick={() => triggerCodeLogin(b.phoneNumber)}
                >
                  📨 Send Login Code
                </Button>
                <Button
                  className="secondary"
                  onClick={() => changeAccountName(b.phoneNumber)}
                >
                  ✏️ Change Name
                </Button>
                <Button
                  className="secondary"
                  onClick={() => changeAccountAvatar(b.phoneNumber)}
                >
                  🖼️ Change Avatar
                </Button>
                <Button
                  className="secondary"
                  onClick={async () => {
                    if (
                      !confirm(
                        "Are you sure you want to clear all credentials for this WhatsApp number? This cannot be undone.",
                      )
                    ) return;
                    const token = await getToken(b.phoneNumber);
                    const promise = client({
                      endpoint: "clearCredentials",
                      payload: { phoneNumber: b.phoneNumber, token },
                    });
                    await toast.promise(promise, {
                      loading: "Clearing credentials...",
                      success: (res) =>
                        res?.success
                          ? "Credentials cleared"
                          : (res?.error || "Failed to clear credentials"),
                      error: (e) => e?.message || "Failed to clear credentials",
                    });
                  }}
                >
                  🔄 Reset
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const SignedInHome = () => (
  <div style={wrapperStyle}>
    <Header />
    <div style={{ overflowY: "auto", flex: 1 }}>
      <UserProfile />
      <WhatsappAccounts />
      <div
        style={{ display: "flex", justifyContent: "center", padding: "20px" }}
      >
        <RelatedServices />
      </div>
    </div>
  </div>
);

const DocsPage = () => (
  <div style={wrapperStyle}>
    <Header />
    <ApiDocs />
  </div>
);

effect(() => {
  if (!emailToCreateUserFor.value) return;
  console.log("Creating user for email", emailToCreateUserFor.value);
  transact(
    tx.users[id()].update({
      lastSeen: Date.now(),
      pushSubscriptions: {},
      email: emailToCreateUserFor.value,
    }),
  );
});

const routes = [
  { path: "/", component: SignedInHome },
  { path: "/docs", component: DocsPage },
];

const NotFound = () => <div>Page not found :(</div>;

const App = () => {
  const { isLoading, error, user } = useAuth();
  useEffect(() => {
    if (user && userIdDeFacto.value) {
      transact(
        tx.users[userIdDeFacto.value].update({ lastSeen: Date.now() }),
      );
    }
  }, [user]);
  if (error) return <ErrorMessage error={error} />;
  if (isLoading) return <LoadingIndication text="Loading your user data..." />;
  if (!user) {
    return (
      <LocationProvider>
        <Toaster toastOptions={{ duration: 4000 }} />
        <ErrorBoundary>
          <Login />
        </ErrorBoundary>
      </LocationProvider>
    );
  }
  if (user) {
    const { error, isLoading, data } = useQuery({
      users: { $: { where: { email: user.email ?? "" } } },
    });
    if (error) {
      return <ErrorMessage error={error} />;
    }
    if (isLoading) {
      return <LoadingIndication text="Loading your user data..." />;
    }
    if (!data.users.length && user.email) {
      emailToCreateUserFor.value = user.email;
      return <LoadingIndication text="Loading your user data..." />;
    }
    emailToCreateUserFor.value = null;
    userIdDeFacto.value = data.users[0].id;
  }
  return (
    <LocationProvider>
      <Toaster toastOptions={{ duration: 4000 }} />
      <ErrorBoundary>
        <Router>
          {routes.map(({ path, component }) => (
            <Route path={path} component={component} />
          ))}
          <Route default component={NotFound} />
        </Router>
      </ErrorBoundary>
    </LocationProvider>
  );
};

render(<App />, coerce(document.getElementById("app")));
